/**
 * Shared phone input with automatic (123) 456-7890 formatting.
 *
 * Accepts raw digit input, caps at 10 digits, and maintains a hidden
 * field with the plain 10-digit value so form submission payloads stay
 * clean. Designed to drop in wherever a <input type="tel"> was used.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  stripNonDigits,
  stripPhonePrefix,
  normalizePhoneDigits,
  formatPhoneDisplay,
} from './form-validation';

export interface PhoneInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  /** Form field name used in FormData. The hidden input carries this name. */
  name: string;
  /** Initial raw digit value (e.g. from saved form state). */
  initialValue?: string;
  /** Called when the raw digit string changes. */
  onValueChange?: (digits: string) => void;
  /** Called on blur with the current raw digit string. */
  onFieldBlur?: (digits: string) => void;
}

const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ name, initialValue = '', onValueChange, onFieldBlur, ...props }, ref) => {
    const [digits, setDigits] = useState(() => normalizePhoneDigits(initialValue));
    const inputRef = useRef<HTMLInputElement | null>(null);
    const cursorRef = useRef<number | null>(null);

    // Sync forwarded ref with internal ref
    const setRefs = useCallback(
      (el: HTMLInputElement | null) => {
        inputRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
      },
      [ref],
    );

    // Restore cursor after React re-render
    useEffect(() => {
      if (cursorRef.current !== null && inputRef.current) {
        inputRef.current.setSelectionRange(cursorRef.current, cursorRef.current);
        cursorRef.current = null;
      }
    });

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        // Browser autofill and contact-card fills often arrive as "+1 678 823 0350".
        // Drop the country code first so the 10-digit cap trims nothing real.
        const inputDigits = stripNonDigits(e.target.value);
        const kept = stripPhonePrefix(inputDigits);
        const raw = kept.slice(0, 10);
        setDigits(raw);
        onValueChange?.(raw);

        // Estimate cursor position in the formatted output, discounting any
        // prefix digits that were dropped from the front of the value.
        const selStart = e.target.selectionStart ?? 0;
        const beforeCursor = e.target.value.slice(0, selStart);
        const droppedLeading = inputDigits.length - kept.length;
        const digitsBeforeCursor = Math.min(
          Math.max(stripNonDigits(beforeCursor).length - droppedLeading, 0),
          raw.length,
        );
        const formatted = formatPhoneDisplay(raw);

        let pos = 0;
        let seen = 0;
        while (pos < formatted.length && seen < digitsBeforeCursor) {
          if (/\d/.test(formatted[pos])) seen++;
          pos++;
        }
        cursorRef.current = pos;
      },
      [onValueChange],
    );

    const handleBlur = useCallback(
      () => { onFieldBlur?.(digits); },
      [digits, onFieldBlur],
    );

    return (
      <>
        <input type="hidden" name={name} value={digits} />
        <input
          {...props}
          ref={setRefs}
          type="tel"
          autoComplete="tel"
          data-field-name={name}
          inputMode="numeric"
          value={formatPhoneDisplay(digits)}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </>
    );
  },
);
PhoneInput.displayName = 'PhoneInput';

export { PhoneInput };
